import * as React from 'react';
import { CalendarItemsConfig } from '../interfaces/CalendarItemsConfig';
import { observer } from 'mobx-react';
import { SurfaceDecorator, SurfaceEventHandlers } from '../interfaces/SurfaceEventHandlers';

/**
 * Wraps provided content into component for injecting decorator and events hadlers
 * {React.CSSProperties} style - the css style to inject
 * {any} key - key to use for component
 * {SurfaceEventHandlers} eventHandlers - Event handlers to inject
 * {SurfaceDecorator<T>} decorator - The decorator function for wrapped component
 * {T} model - the model object where {T} is a type of model
 * inputRef - the cllback for providing reference to decorator element
 */
export function wrapToGridItem<T>(
    style: React.CSSProperties,
    key: React.Key,
    itemConfig: CalendarItemsConfig<T>,
    model: T,
    content: JSX.Element,
    itemIndex?: number,
    tabId?: string,
    hasConflicts?: boolean
): JSX.Element {
    let eventHandlers = itemConfig && itemConfig.itemsEventHandlers;
    if (eventHandlers) {
        style = { ...style, ...{ cursor: 'pointer' } };
    }
    return React.createElement(
        GridItem,
        {
            style: style,
            key: key,
            eventHandlers: eventHandlers,
            decorator: itemConfig ? itemConfig.itemsDecorator : null,
            target: model,
            itemIndex: itemIndex,
            tabId: tabId,
            hasConflicts: hasConflicts || false, // since this parameter is optional, identifies if the event conflicts with any other conflicting event
        },
        content
    );
}

/**
 * Wraps grid item containing the overflow indicator with a div to ensure
 * that outer div takes the position similar to other grid items while
 * only the grid item containing the overflow indicator is clickable.
 *
 * {React.CSSProperties} wrapperStyle - to position the outside div
 * {React.CSSProperties} gridItemStyle - to center align grid item wrt outer div
 * {any} key - key to use for component
 * {itemConfig} The items configuration
 * {T} model - the model object where {T} is a type of model
 * inputRef - the callback for providing reference to decorator element
 */
export function wrapOverflowIndicatorToGridItem<T>(
    wrapperStyle: React.CSSProperties,
    gridItemStyle: React.CSSProperties,
    key: React.Key,
    itemConfig: CalendarItemsConfig<T>,
    model: T,
    content: JSX.Element,
    itemIndex?: number,
    tabId?: string
): JSX.Element {
    return React.createElement(
        'div',
        {
            style: wrapperStyle,
            key: key,
        },
        React.createElement(
            GridItem,
            {
                style: gridItemStyle,
                key: key,
                eventHandlers: itemConfig ? itemConfig.itemsEventHandlers : null,
                decorator: itemConfig ? itemConfig.itemsDecorator : null,
                target: model,
                itemIndex: itemIndex,
                tabId: tabId,
            },
            content
        )
    );
}

/**
 * GridItem properties
 * {React.CSSProperties} style - the css style to inject
 * {SurfaceEventHandlers} eventHandlers - Event handlers to inject
 * {SurfaceDecorator<T>} decorator - The decorator function for wrapped component
 * {T} target - the model object where {T} is a type of model
 * inputRef - the cllback for providing reference to decorator element
 */
export interface GridItemProps<T> {
    style: React.CSSProperties;
    eventHandlers: SurfaceEventHandlers;
    decorator: SurfaceDecorator<T>;
    target: T;
    itemIndex: number;
    tabId: string;
    inputRef?: (element: any) => void;
    hasConflicts?: boolean; // this determines if the item conflicts with any other visible event and will be needed in case it does for ItemPeek to rendered covering the target or not
}

/**
 * This component injects decorators and event hadlers for wrapped component
 */
@observer
export default class GridItem<T> extends React.Component<GridItemProps<T>, {}> {
    private boundEventHandlers: SurfaceEventHandlers;

    constructor(props: GridItemProps<T>) {
        super(props);
        this.bindEventHandlers(props);
    }

    render() {
        let { decorator } = this.props;
        let decoratorElement = decorator ? decorator(this.props.target) : <div />;
        return React.cloneElement(
            decoratorElement,
            {
                'data-itemindex': this.props.itemIndex,
                'data-tabid': this.props.tabId,
                'data-conflict': this.props.hasConflicts ? 1 : 0,
                style: { ...this.props.style, ...decoratorElement.props.style },
                ...this.boundEventHandlers,
                ref: this.props.inputRef,
            },
            [this.props.children]
        );
    }

    componentWillReceiveProps(nextProps: GridItemProps<T>) {
        this.bindEventHandlers(nextProps);
    }

    private bindEventHandlers(props: GridItemProps<T>) {
        this.boundEventHandlers = {};
        if (props.eventHandlers) {
            Object.keys(props.eventHandlers).forEach(key => {
                this.boundEventHandlers[key] = props.eventHandlers[key].bind(props.target);
            });
        }
    }
}
